import React, { useEffect, useRef, useState } from "react"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import {
  faArrowUpRightFromSquare,
  faClose,
  faCommentDollar,
  faCreditCard,
  faFile,
  faFileCode,
  faFileInvoiceDollar,
  faIdCard,
  faPlus,
  faPrint,
  faRotate,
  faSignature,
  faLocationDot,
  faUser,
  faUtensils,
  faWarning,
  faWineGlass,
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Button, { ButtonIcon } from "../../../components/Button/Button"
import GetSpace from "../../../utils/space"
import { TitleChildrenPath, TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import { employeeMemory } from "../../../Objects/Memory/Memory"
import { OrderEntity } from "../../../Objects/Order/OrderEntity"
import {
  CardEmployeeRegister,
  CardEmployeeRegisterInLine,
} from "../../../components/CardEmployeeRegister/CardEmployeeRegister"
import { DetailsItems } from "../../../components/DetailsItems/DetailsItems"
import { convertToLocalMoney } from "../../../Objects/Generics/Helps"
import { EMoneyConditionType } from "../../../Enum/EMoneyConditionType"
import THEME from "../../../configuration/Theme"
import { SIZE } from "../../../configuration/Sizes"
import ModalPortal from "../../../components/Modal/ModalPortal"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import { useTranslation } from "react-i18next"
import i18next from "i18next"
import { BasicCard } from "../../../components/BasicCard/BasicCard"
import { CanView } from "../../../components/CanView/CanView"
import { Permissions } from "../../../Enum/PermissionsType"
import { CalculateView } from "./Components/CalculateView"
import { CancelOrderPupop } from "./CancelOrderPupop"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"
import { OrderPrintOptionsPupop } from "../../../components/OrderPrintOptionsPupop/OrderPrintOptionsPupop"
import { FiscalDocumentPrintPopup } from "../../../components/FiscalIntegration/FiscalDocumentPrintPopup"
import { EPaymentType } from "../../../Enum/EPaymentType"
import { useEmployerLogged } from "../../../context/useEmployeeContext"
import { useMessage } from "../../../context/useMessageContext"
import { useFiscalIntegration } from "../../../hooks/FiscalIntegration/useFiscalIntegration"
import { EFiscalDocumentStatus } from "../../../Objects/FiscalIntegration/EFiscalDocumentStatus"
import { type IFiscalDocument } from "../../../Objects/FiscalIntegration/IFiscalDocument"
import {
  getFiscalDocumentStatusTranslationKey,
  isIssuedFiscalDocumentStatus,
} from "../../../Objects/FiscalIntegration/fiscalDocumentStatus"
import InputField from "../../../components/InputField/InputField"
import { CPF_DIGITS_LENGTH, isValidCpf, normalizeCpf } from "../../../utils/documentFormat"
import { GenericDropDown } from "../../../components/Dropdown/Dropdown"
import { getFiscalPaymentTypeOptions } from "../../../Objects/Order/paymentTypeOptions"

const renderProviderChargeReference = (externalReference?: string) => {
  if (!externalReference) {
    return null
  }

  return (
    <div className="rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">
      <span className="font-semibold">{i18next.t("order:label_provider_external_reference")} </span>

      <span className="break-all font-mono">{externalReference}</span>
    </div>
  )
}

interface OrderPupopCloseProbs {
  order: OrderEntity
  publicView?: boolean
  onClonse: () => void
  onSave?: () => void | Promise<void>
}

export const OrderPupopClose: React.FC<OrderPupopCloseProbs> = ({ publicView, order, onClonse, onSave }) => {
  const { t } = useTranslation(["order", "fiscalIntegration"])

  const [isCancel, setIsCancel] = useState(false)

  const [isPrintTargetModalOpen, setIsPrintTargetModalOpen] = useState(false)

  const [fiscalDocument, setFiscalDocument] = useState<IFiscalDocument | null>(null)

  const [fiscalPrintDocument, setFiscalPrintDocument] = useState<IFiscalDocument | null>(null)

  const [shouldDisplayFiscalSection, setShouldDisplayFiscalSection] = useState(false)

  const [isFiscalIntegrationReady, setIsFiscalIntegrationReady] = useState(false)

  const [isFiscalEmissionAllowed, setIsFiscalEmissionAllowed] = useState(false)

  const [isFiscalStatusLoading, setIsFiscalStatusLoading] = useState(false)

  const [isFiscalStatusUnavailable, setIsFiscalStatusUnavailable] = useState(false)

  const [isFiscalEmissionLoading, setIsFiscalEmissionLoading] = useState(false)

  const [isFiscalActionLoading, setIsFiscalActionLoading] = useState(false)

  const [consumerCpf, setConsumerCpf] = useState("")

  const [requiresFiscalPaymentMethod, setRequiresFiscalPaymentMethod] = useState(false)

  const [fiscalPaymentType, setFiscalPaymentType] = useState<EPaymentType>(EPaymentType.None)

  const { userEmployee } = useEmployerLogged()

  const { setMessage } = useMessage()

  const fiscal = useFiscalIntegration()

  const fiscalRef = useRef(fiscal)

  const shouldLoadFiscalStatus = Boolean(userEmployee?.isMaster && !publicView)

  const hasBlockingFiscalDocument = fiscalDocument !== null && isFiscalIntegrationReady && !isFiscalEmissionAllowed
  const isConsumerCpfValid = consumerCpf.length === 0 || isValidCpf(consumerCpf)

  const shouldShowFiscalEmissionForm =
    shouldDisplayFiscalSection && isFiscalEmissionAllowed && !isFiscalStatusLoading && !isFiscalStatusUnavailable

  const hasValidFiscalPaymentMethod =
    !requiresFiscalPaymentMethod || fiscalPaymentType !== EPaymentType.None

  const canEmitFiscalDocument =
    shouldShowFiscalEmissionForm &&
    !isFiscalEmissionLoading &&
    isConsumerCpfValid &&
    hasValidFiscalPaymentMethod

  useEffect(() => {
    fiscalRef.current = fiscal
  }, [fiscal])

  useEffect(() => {
    setFiscalDocument(null)

    setFiscalPrintDocument(null)

    setShouldDisplayFiscalSection(false)

    setIsFiscalIntegrationReady(false)

    setIsFiscalEmissionAllowed(false)

    setIsFiscalStatusUnavailable(false)

    setConsumerCpf("")

    setRequiresFiscalPaymentMethod(false)

    setFiscalPaymentType(EPaymentType.None)

    if (!shouldLoadFiscalStatus) {
      setIsFiscalStatusLoading(false)
      return
    }

    let isActive = true
    setIsFiscalStatusLoading(true)

    void fiscalRef.current
      .getOrderEmissionStatus(order.id, false)
      .then((emissionStatus) => {
        if (!isActive) {
          return
        }

        setShouldDisplayFiscalSection(emissionStatus.shouldDisplay)

        setIsFiscalIntegrationReady(emissionStatus.isIntegrationReady)

        setIsFiscalEmissionAllowed(emissionStatus.canEmit)

        setRequiresFiscalPaymentMethod(emissionStatus.requiresPaymentMethod)

        setFiscalDocument(emissionStatus.document)

        if (emissionStatus.document && isIssuedFiscalDocumentStatus(emissionStatus.document.status)) {
          order.hasIssuedFiscalDocument = true
        }
      })
      .catch(() => {
        if (isActive) {
          setIsFiscalStatusUnavailable(true)
        }
      })
      .finally(() => {
        if (isActive) {
          setIsFiscalStatusLoading(false)
        }
      })

    return () => {
      isActive = false
    }
  }, [order, shouldLoadFiscalStatus])

  const handleSaved = async () => {
    onClonse()

    await onSave?.()
  }

  const handleEmitFiscalDocument = async () => {
    if (!canEmitFiscalDocument) {
      return
    }

    setIsFiscalEmissionLoading(true)

    try {
      const document = await fiscal.emitOrder(
        order.id,
        consumerCpf,
        requiresFiscalPaymentMethod ? fiscalPaymentType : undefined,
      )

      if (!document) {
        return
      }

      setFiscalDocument(document)

      if (
        requiresFiscalPaymentMethod &&
        order.orderPaymentEntity.ePaymentType === EPaymentType.None
      ) {
        order.orderPaymentEntity.ePaymentType = fiscalPaymentType
      }

      if (isIssuedFiscalDocumentStatus(document.status)) {
        order.hasIssuedFiscalDocument = true
      }

      setIsFiscalEmissionAllowed(false)
      setMessage(t("fiscalIntegration:feedback.successTitle"), t("fiscalIntegration:feedback.emissionQueued"))
    } catch {
      return
    } finally {
      setIsFiscalEmissionLoading(false)
    }
  }

  const handleRefreshFiscalDocument = async () => {
    if (!fiscalDocument || isFiscalActionLoading) {
      return
    }

    setIsFiscalActionLoading(true)

    try {
      const refreshedDocument = await fiscal.refreshDocument(fiscalDocument.id)

      if (refreshedDocument) {
        setFiscalDocument(refreshedDocument)

        if (isIssuedFiscalDocumentStatus(refreshedDocument.status)) {
          order.hasIssuedFiscalDocument = true
        }
      }

      setMessage(t("fiscalIntegration:feedback.successTitle"), t("fiscalIntegration:feedback.refreshQueued"))
    } catch {
      return
    } finally {
      setIsFiscalActionLoading(false)
    }
  }

  const handleDownloadFiscalXml = async () => {
    if (!fiscalDocument || isFiscalActionLoading) {
      return
    }

    setIsFiscalActionLoading(true)

    try {
      await fiscal.downloadXml(fiscalDocument)
    } finally {
      setIsFiscalActionLoading(false)
    }
  }

  const handlePrintFiscalDocument = async (documentId: number, printerConfigurationId: number) => {
    await fiscal.printDocument(documentId, printerConfigurationId)
    setFiscalPrintDocument(null)
    setMessage(t("fiscalIntegration:feedback.successTitle"), t("fiscalIntegration:feedback.printQueued"))
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert}>
            <TitlePageResponsiveIconSmall
              iconColor={THEME.colors.iconBGColor}
              title={t("title_close_command")}
              icon={faCommentDollar}
              children={
                <div className={defaultStyle.ButtonsActions} style={{ maxWidth: "380px" }}>
                  <>
                    <PopupHelpButton
                      helpKey="pages.fluxo.order.orderpupopclose.1"
                      variant="global"
                      title={t("helpPopup:global.command.title")}
                      description={t("helpPopup:global.command.description")}
                      bullets={t("helpPopup:global.command.bullets", { returnObjects: true }) as string[]}
                    />

                    <GetSpace right={5} />

                    <ButtonIcon
                      typeButton="button"
                      sizeNumber={SIZE.buttonCloseIconSize}
                      bgColor={THEME.colors.buttonBGSaveColor}
                      iconColor={THEME.colors.buttonFGSaveColor}
                      icon={faPrint}
                      onClick={() => setIsPrintTargetModalOpen(true)}
                      size="small"
                      descriptionKey="interactionDescriptions:pages.fluxo.order.orderPupopClose.button.setIsPrintTargetModalOpenTrue"
                    />

                    <GetSpace left={5} />

                    <ButtonIcon
                      size="small"
                      icon={faClose}
                      typeButton="button"
                      sizeNumber={SIZE.buttonCloseIconSize}
                      bgColor={THEME.colors.buttonBGCloseColor}
                      iconColor={THEME.colors.buttonFGCloseColor}
                      onClick={onClonse}
                      descriptionKey="interactionDescriptions:pages.fluxo.order.orderPupopClose.button.onClonse"
                    />
                  </>
                </div>
              }
            />

            <GetSpace top={5} />

            {order.isServicePointSelfServiceOrder() && (
              <>
                <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
                  <div className="rounded-lg border-2 border-violet-300 bg-violet-50 px-4 py-3 text-center text-xs font-black uppercase tracking-wider text-violet-700">
                    {t("label_service_point_self_service_order")}
                  </div>
                </Animate>

                <GetSpace top={10} />
              </>
            )}

            {order.isCancel && (
              <>
                <GetSpace top={10} />

                <TitlePageResponsiveIconSmall
                  icon={faWarning}
                  sizeProp="2x"
                  boxShadow="none"
                  title={t("title_close_canceled_command")}
                  iconColor={THEME.colors.iconBGAlertColor}
                />

                {!publicView && (
                  <>
                    <GetSpace top={10} />

                    <TitleChildrenPath
                      children={
                        <>
                          <CardEmployeeRegister
                            employee={employeeMemory.findByStringId(order.orderCanceledEntity!.employeeId)!}
                            date={order.orderCanceledEntity!.date}
                            icon={faUser}
                          />

                          <GetSpace top={5} />

                          <TitleChildrenPath
                            title={t("label_reasor_cancel")}
                            icon={faFile}
                            iconColor="red"
                            children={
                              <>
                                <GetSpace top={10} />

                                <textarea
                                  className="w-full rounded-[5px] text-[12px] pt-[10px] pl-[15px] font-bold"
                                  value={order.orderCanceledEntity!.reason}
                                  data-interaction-description-key="interactionDescriptions:pages.fluxo.order.orderPupopClose.input.input"
                                />
                              </>
                            }
                          />
                        </>
                      }
                    />
                  </>
                )}

                <GetSpace top={25} />
              </>
            )}

            <GetSpace top={10} />

            {order.orderPaymentEntity.isOnlinePayment &&
              renderProviderChargeReference(order.orderPaymentEntity.externalReference)}

            <GetSpace top={10} />

            <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
              <section className="rounded-xl border-2 border-slate-200 bg-slate-50 px-4 py-3">
                <div className="flex items-center gap-2 text-sm text-slate-700">
                  <FontAwesomeIcon icon={faCreditCard} className="text-orange-600" />

                  <span className="font-semibold">{t("label_payment_method")}</span>

                  <strong className="ml-auto text-slate-900">
                    {order.orderPaymentEntity.ePaymentType.toPaymentTypeString()}
                  </strong>
                </div>

                {order.orderPaymentEntity.ePaymentType === EPaymentType.Cash &&
                  order.orderPaymentEntity.amountPaid !== undefined && (
                    <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                      <div className="rounded-lg bg-white p-2">
                        <span className="block text-slate-500">{t("label_amount_received")}</span>

                        <strong>{convertToLocalMoney(order.orderPaymentEntity.amountPaid)}</strong>
                      </div>

                      <div className="rounded-lg bg-white p-2">
                        <span className="block text-slate-500">{t("label_change")}</span>

                        <strong>{convertToLocalMoney(order.orderPaymentEntity.changeAmount)}</strong>
                      </div>
                    </div>
                  )}
              </section>
            </Animate>

            <GetSpace top={10} />

            <CardEmployeeRegisterInLine
              employee={employeeMemory.findByStringId(order.orderPaymentEntity.employeeEntityId)!}
              date={order.orderPaymentEntity.date}
              icon={faUser}
            />

            <GetSpace top={10} />

            <TitleChildrenPath
              title={order.getCustomerDisplayName()}
              icon={faSignature}
              iconColor="black"
              boxShadow="none"
              keyCode={"Costumer-payment"}
              subtitleChildren={<BasicCard borderColor={THEME.colors.white} title={order.getCustomerDisplayCpf()} />}
            />

            <GetSpace top={5} />

            {order.servicePointEntity && (
              <TitleChildrenPath
                title={t("components:label_servicePoints")}
                icon={faLocationDot}
                boxShadow="none"
                subtitleChildren={<BasicCard borderColor={THEME.colors.white} title={order.servicePointEntity.name} />}
              />
            )}

            <div className={defaultStyle.divisionBar}></div>

            {order.orderPaymentEntity.description.length > 0 && (
              <>
                <GetSpace top={5} />

                <TitleChildrenPath
                  title={t("components:label_details")}
                  icon={faFile}
                  boxShadow="none"
                  children={
                    <textarea
                      onChange={() => {}}
                      className={defaultStyle.textarea}
                      value={order.orderPaymentEntity.description}
                      data-interaction-description-key="interactionDescriptions:pages.fluxo.order.orderPupopClose.input.input2"
                    />
                  }
                />
              </>
            )}

            <GetSpace top={25} />

            {order.orderProducts.map((e, index) => {
              return (
                <React.Fragment key={index}>
                  <DetailsItems
                    iconColor={THEME.colors.iconBGColor}
                    employeeId={e.employeeId}
                    date={e.date}
                    name={e.productEntity.catalogProductEntity.name}
                    details={e.description}
                    coast={e.valuePaid}
                    icon={faUtensils}
                    canShowAll={true}
                    addtionals={e.addtionals}
                    selections={e.selections ?? []}
                  />

                  <GetSpace top={5} />
                </React.Fragment>
              )
            })}

            <GetSpace top={5} />

            {order.orderCatalogItems.length > 0 && (
              <>
                {order.orderCatalogItems.map((e) => {
                  return (
                    <DetailsItems
                      iconColor={THEME.colors.iconBGColor}
                      employeeId={e.employeeId}
                      date={e.date}
                      name={e.catalogItemEntity.catalogProductEntity.name}
                      details={e.description}
                      coast={Number.parseFloat(e.getValueWithAddtions().toString())}
                      icon={faWineGlass}
                      canShowAll={true}
                      addtionals={e.addtionals}
                      selections={e.selections ?? []}
                    />
                  )
                })}
              </>
            )}

            <div className={defaultStyle.divisionBar}></div>

            {order.extraServices.length > 0 && (
              <>
                {order.extraServices.map((e) => {
                  return (
                    <>
                      <TitlePageResponsiveIconSmall
                        title={e.name}
                        icon={faPlus}
                        iconColor="red"
                        boxShadow="none"
                        children={
                          <BasicCard
                            borderColor={THEME.colors.white}
                            title={
                              e.conditionType === EMoneyConditionType.Percentage
                                ? `${e.valueBase}%`
                                : convertToLocalMoney(e.valueBase)
                            }
                          />
                        }
                      />

                      <GetSpace top={2} />
                    </>
                  )
                })}
              </>
            )}

            <div className={defaultStyle.divisionBar}></div>

            <CalculateView
              valueBaseProducts={order.orderPaymentEntity.totalProducts}
              valueBaseCatalogItems={order.orderPaymentEntity.totalCatalogItems}
              valueAddtionals={order.orderPaymentEntity.totalAddtionals}
              valueBaseSum={order.orderPaymentEntity.totalNotLiquid}
              valueTotalDiscount={order.orderPaymentEntity.totalDiscount}
              valueBaseSumWithDiscount={order.orderPaymentEntity.totalBase}
              valueBaseExtraServices={order.orderPaymentEntity.totalExtra}
              valueTotalAwards={order.orderPaymentEntity.totalAward}
              valueTotalCoupons={order.orderPaymentEntity.totalCupom}
              valueTotalShippingFee={order.orderPaymentEntity.shippingFee}
              valueFinalWithoutAwards={order.orderPaymentEntity.totalFinal}
              valueFinal={order.orderPaymentEntity.totalFinal}
              awards={order.associatedAwards}
            />

            {shouldDisplayFiscalSection && !isFiscalStatusLoading && !isFiscalStatusUnavailable && (
              <>
                <div className={defaultStyle.divisionBar}></div>

                <Animate type={AnimationTypes.fadeInTraslate} duration={"1.5s"}>
                  <section className="rounded-xl border-2 border-orange-200 bg-orange-50 p-4">
                    <div className="flex items-start gap-3">
                      <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-orange-600 text-white">
                        <FontAwesomeIcon icon={faFileInvoiceDollar} />
                      </span>

                      <div className="min-w-0 flex-1">
                        <strong className="block text-sm text-orange-950">
                          {t("fiscalIntegration:orderPopup.title")}
                        </strong>

                        <p className="mt-1 text-xs leading-relaxed text-orange-900">
                          {t(
                            isFiscalEmissionAllowed
                              ? "fiscalIntegration:orderPopup.description"
                              : "fiscalIntegration:orderPopup.existingDescription",
                          )}
                        </p>

                        <div className="mt-3 rounded-lg border border-orange-200 bg-white px-3 py-2 text-xs text-slate-700">
                          <span className="font-semibold">{t("fiscalIntegration:orderPopup.status")} </span>

                          <strong>
                            {fiscalDocument
                              ? t(`fiscalIntegration:${getFiscalDocumentStatusTranslationKey(fiscalDocument.status)}`)
                              : t("fiscalIntegration:orderPopup.notIssued")}
                          </strong>
                        </div>

                        {hasBlockingFiscalDocument && (
                          <p className="mt-2 text-xs font-semibold text-orange-900">
                            {t("fiscalIntegration:orderPopup.alreadyRequestedDescription")}
                          </p>
                        )}
                      </div>
                    </div>

                    {fiscalDocument?.cancellationError && (
                      <div
                        className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900"
                        role="alert"
                      >
                        <strong className="block">{t("fiscalIntegration:orderPopup.cancellationRejected")}</strong>
                        <span>{fiscalDocument.cancellationError}</span>
                      </div>
                    )}

                    {fiscalDocument && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        <button
                          type="button"
                          disabled={isFiscalActionLoading}
                          onClick={() => void handleRefreshFiscalDocument()}
                          className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
                          title={t("fiscalIntegration:documents.refresh")}
                          data-interaction-description-key="interactionDescriptions:pages.fluxo.order.orderPupopClose.button.refreshFiscalDocument"
                        >
                          <FontAwesomeIcon icon={faRotate} />
                          {t("fiscalIntegration:documents.refresh")}
                        </button>

                        {fiscalDocument.hasDanfce && fiscalDocument.danfceUrl && (
                          <button
                            type="button"
                            disabled={isFiscalActionLoading}
                            onClick={() => fiscal.openDanfce(fiscalDocument)}
                            className="inline-flex items-center gap-2 rounded-lg border border-sky-200 bg-sky-50 px-2.5 py-1.5 text-xs font-bold text-sky-800 hover:bg-sky-100 disabled:cursor-not-allowed disabled:opacity-60"
                            title={t("fiscalIntegration:documents.openDanfce")}
                            data-interaction-description-key="interactionDescriptions:pages.fluxo.order.orderPupopClose.button.openDanfce"
                          >
                            <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                            {t("fiscalIntegration:documents.fiscalReceipt")}
                          </button>
                        )}

                        {fiscalDocument.canPrint && (
                          <button
                            type="button"
                            disabled={isFiscalActionLoading}
                            onClick={() => setFiscalPrintDocument(fiscalDocument)}
                            className="inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-xs font-bold text-emerald-800 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-60"
                            title={t("fiscalIntegration:documents.printDanfce")}
                            data-interaction-description-key="interactionDescriptions:pages.fluxo.order.orderPupopClose.button.printDanfce"
                          >
                            <FontAwesomeIcon icon={faPrint} />
                            {t("fiscalIntegration:documents.print")}
                          </button>
                        )}

                        {fiscalDocument.hasXml && (
                          <button
                            type="button"
                            disabled={isFiscalActionLoading}
                            onClick={() => void handleDownloadFiscalXml()}
                            className="inline-flex items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-2.5 py-1.5 text-xs font-bold text-indigo-800 hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-60"
                            title={t("fiscalIntegration:documents.downloadXml")}
                            data-interaction-description-key="interactionDescriptions:pages.fluxo.order.orderPupopClose.button.downloadXml"
                          >
                            <FontAwesomeIcon icon={faFileCode} />
                            {t("fiscalIntegration:documents.xml")}
                          </button>
                        )}
                      </div>
                    )}

                    {shouldShowFiscalEmissionForm && (
                      <div className="mt-5 space-y-3">
                        {requiresFiscalPaymentMethod && (
                          <div className="space-y-1">
                            <GenericDropDown
                              legend={t("fiscalIntegration:paymentMethod.label")}
                              initialState={fiscalPaymentType.toString()}
                              data={[
                                {
                                  value: EPaymentType.None.toString(),
                                  label: t("fiscalIntegration:paymentMethod.placeholder"),
                                },
                                ...getFiscalPaymentTypeOptions(),
                              ]}
                              handlerFuncion={(event) =>
                                setFiscalPaymentType(Number(event.target.value) as EPaymentType)
                              }
                              errorMessage={
                                fiscalPaymentType === EPaymentType.None
                                  ? t("fiscalIntegration:paymentMethod.required")
                                  : undefined
                              }
                              descriptionKey="interactionDescriptions:pages.fluxo.order.orderPupopClose.select.fiscalPaymentMethod"
                            />

                            <p className="px-1 text-xs leading-relaxed text-orange-900">
                              {t("fiscalIntegration:paymentMethod.help")}
                            </p>
                          </div>
                        )}

                        <div className="space-y-1">
                          <InputField
                            type="text"
                            icon={faIdCard}
                            value={consumerCpf}
                            inputMode="numeric"
                            maxLength={CPF_DIGITS_LENGTH}
                            pattern="\d{11}"
                            placeholder={t("fiscalIntegration:orderPopup.consumerCpf")}
                            onInvalidMessage={t("fiscalIntegration:orderPopup.consumerCpfInvalid")}
                            errorMessage={
                              consumerCpf.length > 0 && !isConsumerCpfValid
                                ? t("fiscalIntegration:orderPopup.consumerCpfInvalid")
                                : undefined
                            }
                            onChange={(event) => setConsumerCpf(normalizeCpf(event.target.value))}
                            descriptionKey="interactionDescriptions:pages.fluxo.order.orderPupopClose.input.consumerCpf"
                          />

                          <p className="px-1 text-xs leading-relaxed text-orange-900">
                            {t("fiscalIntegration:orderPopup.consumerCpfHelp")}
                          </p>
                        </div>

                        <Button
                          label={
                            isFiscalEmissionLoading
                              ? t("fiscalIntegration:orderPopup.emitting")
                              : hasBlockingFiscalDocument
                                ? t("fiscalIntegration:orderPopup.alreadyRequested")
                                : fiscalDocument?.status === EFiscalDocumentStatus.Rejected
                                  ? t("fiscalIntegration:orderPopup.retry")
                                  : fiscalDocument?.status === EFiscalDocumentStatus.Canceled
                                    ? t("fiscalIntegration:orderPopup.emitReplacement")
                                    : t("fiscalIntegration:orderPopup.emit")
                          }
                          typeButton="button"
                          type="primary"
                          size="medium"
                          bgColor={THEME.colors.buttonBGSaveColor}
                          disabled={!canEmitFiscalDocument}
                          onClick={() => void handleEmitFiscalDocument()}
                          descriptionKey="interactionDescriptions:pages.fluxo.order.orderPupopClose.button.emitting"
                        />
                      </div>
                    )}
                  </section>
                </Animate>

                <div className={defaultStyle.divisionBar}></div>
              </>
            )}

            {!order.isCancel && !publicView && (
              <>
                <GetSpace top={10} />

                <CanView permission={Permissions.CanEditeOrders}>
                  <div className={defaultStyle.buttonsController}>
                    <Button
                      label={t("buttons:button_cancel")}
                      typeButton="button"
                      type="primary"
                      size="medium"
                      bgColor={THEME.colors.buttonBGCloseColor}
                      onClick={() => setIsCancel(true)}
                      descriptionKey="interactionDescriptions:pages.fluxo.order.orderPupopClose.button.cancel"
                    />
                  </div>
                </CanView>
              </>
            )}
          </div>
        </Animate>

        {isCancel && (
          <CancelOrderPupop
            icon={faWarning}
            order={order}
            showFiscalCancellationNotice={
              fiscalDocument !== null && fiscalDocument.status !== EFiscalDocumentStatus.Canceled
            }
            onSave={handleSaved}
            onClonse={() => setIsCancel(false)}
          />
        )}
      </div>

      <OrderPrintOptionsPupop
        isOpen={isPrintTargetModalOpen}
        title={t("title_print_target")}
        orderId={order.id}
        printScope="customer"
        onClose={() => setIsPrintTargetModalOpen(false)}
      />

      <FiscalDocumentPrintPopup
        document={fiscalPrintDocument}
        onClose={() => setFiscalPrintDocument(null)}
        onConfirm={handlePrintFiscalDocument}
      />
    </ModalPortal>
  )
}
